import { useNavigate, useParams } from "react-router-dom";
import { PrimaryButton } from "../../components/buttons/PrimaryButton";
import { SecondaryButton } from "../../components/buttons/SecondaryButton";
import Form from "../../components/inputs/Form";
import Header from "../../components/layout/Header";
import { Page, PageBody, PageHeader } from "../../components/layout/Page";
import { addIcon, backArrow, save, trash } from "../../assets/icons";
import Card from "../../components/card/Card";
import "./etalon.css";
import { TextInput } from "../../components/inputs/TextInput";
import { useEffect, useState } from "react";
import { Data } from "../../lib/typeDeclarations";
import { pattern } from "../../lib/pattern";
import { createRecord, deleteRecord, updateRecord } from "../../server/fetchFunctions";
import { useFindById, useListRecords } from "../../server/hooks/getData";
import { Loader } from "../../components/loader/Loader";
import { formatDate } from "../../lib/dateHelper";
import { DateInput } from "../../components/inputs/Datepicker";

export default function EditEtalon() {
  document.title = "Etalon szerkesztése";
  const navigate = useNavigate();
  const id = useParams().id || "";

  const { data: initEtalon, loading: etalonL } = useFindById("etalon", [id], id);
  const { data: etalonFunc, loading: etalonFuncL } = useListRecords("etalonFunc", [], {});
  const { data: si_prefixes, loading: si_prefixesL } = useListRecords("siprefix", [], {});
  const { data: units, loading: unitsL } = useListRecords("units", [], {});
  const { data: paramList, loading: paramsL } = useListRecords("etalonParam", [], {
    etalon_id: id,
    orderBy: "etalon_function_sort_order",
    order: "ASC",
  });

  const [etalon, setEtalon] = useState<Data>({
    type: "",
    description: "",
    manufacturer: "",
    serial_no: "",
    cal_number: "",
    last_calibration: "",
    cal_frequency: "",
  });

  const [etalonParams, setEtalonParams] = useState<{ [key: string]: any }>({
    etalon_function_id: "",
    range: "",
    resolution: "",
    si_prefix_id: "",
    unit_id: "",
    u_precentage: 0,
    u_digit: 0,
    u_value: 0,
    u_standard: 0,
    etalon_id: "",
  });

  const [params, setParams] = useState(paramList.data || []);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!etalonL && !paramsL) {
      setLoading(true);
      setEtalon({
        ...initEtalon,
        last_calibration: formatDate(initEtalon.last_calibration),
      });
      setParams(
        paramList.data.map(item => ({
          ...item,
          range: Number(item.range),
          resolution: Number(item.resolution),
          u_precentage: Number(item.u_precentage),
          u_digit: Number(item.u_digit),
          u_value: Number(item.u_value),
          u_standard: Number(item.u_standard),
        }))
      );
      setLoading(false);
    }
  }, [initEtalon, paramList]);

  //Functions
  const updateEtalon = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    await updateRecord("etalon", id, etalon);
    setLoading(false);
  };

  const handleParamsChange = async (e: any, item: any, property: string) => {
    setParams(prev => prev.map(i => (i.id === item.id ? { ...i, [property]: e.target.value } : i)));
    await updateRecord("etalonParam", item.id, { [property]: e.target.value });
  };

  const addNewParam = async () => {
    const response = await createRecord("etalonParam", {
      ...etalonParams,
      etalon_id: id,
    });
    if (response) {
      setParams(prev => [
        ...prev,
        {
          ...response,
          range: Number(response.range),
          resolution: Number(response.resolution),
          u_precentage: Number(response.u_precentage),
          u_digit: Number(response.u_digit),
          u_value: Number(response.u_value),
          u_standard: Number(response.u_standard),
        },
      ]);
      setEtalonParams({
        ...etalonParams,
        range: "",
        resolution: "",
        u_precentage: 0,
        u_digit: 0,
        u_value: 0,
        u_standard: 0,
        etalon_id: "",
      });
    }
  };

  const deleteParam = async (id: string) => {
    await deleteRecord("etalonParam", id);
    setParams(prev => prev.filter(item => item.id !== id));
  };

  return (
    <Page>
      <Form onSubmit={e => updateEtalon(e)}>
        <Header>
          <SecondaryButton
            btn={{ svgBefore: backArrow, text: "Vissza", onClick: () => navigate(-1) }}
          />
          <PrimaryButton btn={{ svgBefore: save, text: "Ment", type: "submit" }} />
        </Header>
        <PageHeader title="Etalon szerkesztése" />
        {loading ? (
          <Loader />
        ) : (
          <PageBody>
            <div className="device">
              <Card style={{ width: "100%", maxWidth: 800 }} title="Etalon">
                <TextInput
                  stateKey="type"
                  inlineLabel="Típus"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.normal_text}
                  labelWidth={220}
                  required
                />
                <TextInput
                  stateKey="manufacturer"
                  inlineLabel="Gyártó"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.normal_text}
                  labelWidth={220}
                  required
                />

                <TextInput
                  stateKey="description"
                  inlineLabel="Megnevezés"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.normal_text}
                  labelWidth={220}
                  required
                />
                <TextInput
                  stateKey="serial_no"
                  inlineLabel="Gyári szám"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.normal_text}
                  labelWidth={220}
                  required
                />
                <TextInput
                  stateKey="cal_number"
                  inlineLabel="Kalibrálási szám"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.normal_text}
                  labelWidth={220}
                  required
                />
                <DateInput
                  state={etalon}
                  setState={setEtalon}
                  stateKey="last_calibration"
                  inlineLabel="Utolsó kalibrálás dátuma"
                  labelWidth={220}
                  required
                />
                <TextInput
                  stateKey="cal_frequency"
                  inlineLabel="Kalibrálási gyakoriság"
                  state={etalon}
                  setState={setEtalon}
                  pattern={pattern.number}
                  format="toNumber"
                  labelWidth={220}
                  required
                />
              </Card>
              <Card style={{ width: "100%" }} title="Mérési képességek">
                <table className="etalon_params">
                  <thead>
                    <tr>
                      <td>Funkció</td>
                      <td>Tartomány</td>
                      <td>Felbontás</td>
                      <td>Si-előtag</td>
                      <td>Mértékegység</td>
                      <td>U (%)</td>
                      <td>U (digit)</td>
                      <td>U (érték)</td>
                      <td>U (visszavezetett érték)</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <select
                          id="functions"
                          name="functions"
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, etalon_function_id: e.target.value })
                          }
                        >
                          <option value="0">Válasszon...</option>
                          {!etalonFuncL &&
                            etalonFunc.data.map((item: any, index) => (
                              <option key={index} value={item.id}>
                                {item.function}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.range}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, range: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.resolution}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, resolution: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <select
                          id="si_prefix"
                          name="si_prefix"
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, si_prefix_id: e.target.value })
                          }
                        >
                          <option value="0">Válasszon...</option>
                          {!si_prefixesL &&
                            si_prefixes.data.map((item: any, index) => (
                              <option key={index} value={item.id}>
                                {item.symbol}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <select
                          id="units"
                          name="units"
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, unit_id: e.target.value })
                          }
                        >
                          <option value="0">Válasszon...</option>
                          {!unitsL &&
                            units.data.map((item: any, index) => (
                              <option key={index} value={item.id}>
                                {item.symbol}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.u_precentage}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, u_precentage: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.u_digit}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, u_digit: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.u_value}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, u_value: e.target.value })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={etalonParams.u_standard}
                          onChange={e =>
                            setEtalonParams({ ...etalonParams, u_standard: e.target.value })
                          }
                        />
                      </td>
                      <td onClick={addNewParam} style={{ cursor: "pointer" }}>
                        <div
                          className="svg"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginLeft: 5,
                          }}
                        >
                          {addIcon}
                        </div>
                      </td>
                    </tr>
                    {!paramsL &&
                      params
                        .sort((a: any, b: any) => {
                          if (a.etalon_function_sort_order === b.etalon_function_sort_order) {
                            return (
                              a.range *
                                Number(
                                  si_prefixes.data.find(i => i.id === a.si_prefix_id)?.factor || 1
                                ) -
                              b.range *
                                Number(
                                  si_prefixes.data.find(i => i.id === b.si_prefix_id)?.factor || 1
                                )
                            );
                          }
                          return a.etalon_function_sort_order - b.etalon_function_sort_order;
                        })
                        .map((item: any, index) => (
                          <tr key={index}>
                            <td>
                              <select
                                id="functions"
                                name="functions"
                                value={item.etalon_function_id}
                                onChange={e => handleParamsChange(e, item, "etalon_function_id")}
                              >
                                <option value="0">Válasszon...</option>
                                {!etalonFuncL &&
                                  etalonFunc.data.map((item: any, index) => (
                                    <option key={index} value={item.id}>
                                      {item.function}
                                    </option>
                                  ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.range}
                                onChange={e => handleParamsChange(e, item, "range")}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.resolution}
                                onChange={e => handleParamsChange(e, item, "resolution")}
                              />
                            </td>
                            <td>
                              <select
                                id="si_prefix"
                                name="si_prefix"
                                value={item.si_prefix_id}
                                onChange={e => handleParamsChange(e, item, "si_prefix_id")}
                              >
                                <option value="0">Válasszon...</option>
                                {!si_prefixesL &&
                                  si_prefixes.data.map((item: any, index) => (
                                    <option key={index} value={item.id}>
                                      {item.symbol}
                                    </option>
                                  ))}
                              </select>
                            </td>
                            <td>
                              <select
                                id="units"
                                name="units"
                                value={item.unit_id}
                                onChange={e => handleParamsChange(e, item, "unit_id")}
                              >
                                <option value="0">Válasszon...</option>
                                {!unitsL &&
                                  units.data.map((item: any, index) => (
                                    <option key={index} value={item.id}>
                                      {item.symbol}
                                    </option>
                                  ))}
                              </select>
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.u_precentage}
                                onChange={e => handleParamsChange(e, item, "u_precentage")}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.u_digit}
                                onChange={e => handleParamsChange(e, item, "u_digit")}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.u_value}
                                onChange={e => handleParamsChange(e, item, "u_value")}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={item.u_standard}
                                onChange={e => handleParamsChange(e, item, "u_standard")}
                              />
                            </td>
                            <td
                              onClick={() => deleteParam(item.id)}
                              className="delete_row"
                              style={{ cursor: "pointer" }}
                            >
                              <div
                                className="svg"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  marginLeft: 5,
                                }}
                              >
                                {trash}
                              </div>
                            </td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </PageBody>
        )}
      </Form>
    </Page>
  );
}
